import { useCallback, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ModalAtom } from "../../../../components/atoms/atom";
import {
	isTradeAccessIncomplete,
	normalizePermissions,
} from "../../../../constant/projectPermissions";
import useProjectPermission from "../../../../hooks/useProjectPermission";
import { useInviteProjectMembersMutation } from "../../../../store/services/projectMembers";
import { showToast } from "../../../../utils/method";
import Step1SelectUser from "./step1-select-user";
import Step2Permissions from "./step2-permissions";
import WizardStepIndicator from "./wizard-step-indicator";
import "./add-member-wizard.scss";

function AddMemberWizard({
	show,
	onClose,
	projectId,
	existingMemberIds = [],
	onSuccess,
}) {
	const { t } = useTranslation();
	const { isProjectAdmin } = useProjectPermission();
	// Inviting members is Project-Admin-only.
	const canInviteMembers = isProjectAdmin;

	const [step, setStep] = useState(1);
	const [users, setUsers] = useState([]);
	const [permissions, setPermissions] = useState(null);
	const [selectedPresetId, setSelectedPresetId] = useState(null);
	const [presetUsedName, setPresetUsedName] = useState(null);

	const [inviteMembers, { isLoading }] = useInviteProjectMembersMutation();

	const applyPreset = useCallback((preset) => {
		if (!preset) {
			setSelectedPresetId(null);
			setPresetUsedName(null);
			setPermissions(null);
			return;
		}
		setSelectedPresetId(preset.id);
		setPresetUsedName(preset.name);
		setPermissions(normalizePermissions(preset.permissions));
	}, []);

	const resetState = () => {
		setStep(1);
		setUsers([]);
		setPermissions(null);
		setSelectedPresetId(null);
		setPresetUsedName(null);
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const goToStep2 = () => {
		if (!users.length) {
			showToast(t("projects.members.error-user-required"), {
				type: "error",
			});
			return;
		}
		if (!permissions) {
			showToast(t("projects.permissions.error-permissions-required"), {
				type: "error",
			});
			return;
		}
		setStep(2);
	};

	const handleSubmit = async () => {
		if (!canInviteMembers) {
			return;
		}

		if (!permissions) {
			showToast(t("projects.permissions.error-permissions-required"), {
				type: "error",
			});
			return;
		}

		try {
			await inviteMembers({
				projectId,
				users: users.map(
					({ email, firstName, lastName, phoneNumber, company }) => ({
						email,
						firstName,
						lastName,
						...(phoneNumber ? { phoneNumber } : {}),
						...(company ? { company } : {}),
					}),
				),
				permissions: normalizePermissions(permissions),
				presetUsedName: presetUsedName || undefined,
			}).unwrap();

			showToast(t("projects.members.invitations-sent"), { type: "success" });
			handleClose();
			onSuccess?.();
		} catch (error) {
			showToast(error?.data?.message || error?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	const modalTitle =
		step === 1
			? t("projects.members.invite")
			: t("projects.members.configure-permissions-title");

	const footer = (
		<div className="add-member-wizard__mfooter">
			<div className="add-member-wizard__mfooter-left">
				{step === 1 ? (
					<span className="add-member-wizard__mfooter-count">
						{t("projects.members.users-selected-count", {
							count: users.length,
						})}
					</span>
				) : (
					<Button
						variant="secondary"
						className="btn-back"
						onClick={() => setStep(1)}
					>
						&larr; {t("common.back")}
					</Button>
				)}
			</div>
			<div className="add-member-wizard__mfooter-btns">
				<Button variant="link" className="btn-cancel" onClick={handleClose}>
					{t("common.cancel")}
				</Button>
				{step === 1 ? (
					<Button
						className="btn-save"
						onClick={goToStep2}
						disabled={!users.length}
					>
						{t("common.next")} &rarr;
					</Button>
				) : (
					<Button
						className="btn-save"
						onClick={handleSubmit}
						disabled={
							isLoading ||
							!permissions ||
							isTradeAccessIncomplete(permissions)
						}
					>
						{t("projects.members.send-invitations")}
					</Button>
				)}
			</div>
		</div>
	);

	return (
		<ModalAtom
			show={show}
			handleClose={handleClose}
			title={modalTitle}
			enforceFocus={false}
			hideSaveButton
			hideCloseButton
			customClassName="add-member-wizard-modal"
			footer={footer}
			body={
				<div className="add-member-wizard">
					<WizardStepIndicator currentStep={step} />
					<div hidden={step !== 1}>
						<Step1SelectUser
							projectId={projectId}
							existingMemberIds={existingMemberIds}
							selectedUsers={users}
							onSelectedUsersChange={setUsers}
							selectedPresetId={selectedPresetId}
							onPresetSelect={applyPreset}
						/>
					</div>
					{step === 2 && permissions && (
						<Step2Permissions
							projectId={projectId}
							permissions={permissions}
							onPermissionsChange={setPermissions}
							selectedPresetId={selectedPresetId}
							onPresetApplied={applyPreset}
						/>
					)}
				</div>
			}
		/>
	);
}

export default AddMemberWizard;
